import React, { useEffect, useState, useCallback } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import { SignOut, CameraRotate, ChartLineUp, UserCircle, Plant, Bell, Crown, GearSix, ShieldStar, List, SignIn, Sparkle, Tag, EnvelopeSimple, Newspaper, BookOpen } from "@phosphor-icons/react";
import LanguageMenu from "./LanguageMenu";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "./ui/dropdown-menu";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [isPro, setIsPro] = useState(false);

  const refreshHud = useCallback(async () => {
    if (!user || user === false) {
      setUnread(0);
      setIsPro(false);
      return;
    }
    try {
      const [{ data: n }, { data: b }] = await Promise.all([
        api.get("/notifications"),
        api.get("/billing/me"),
      ]);
      setUnread(n.unread || 0);
      setIsPro(!!b.is_pro);
    } catch (_err) {
      // best-effort HUD refresh
    }
  }, [user]);

  useEffect(() => {
    refreshHud();
    if (!user || user === false) return undefined;
    const id = setInterval(refreshHud, 30000);
    return () => clearInterval(id);
  }, [refreshHud, user]);

  const onLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-40 w-full">
      <div className="absolute inset-0 bg-sand-50/85 backdrop-blur-xl border-b border-sand-200" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group" data-testid="navbar-logo">
          <img
            src="/favicon.svg"
            alt="A Must For Earth"
            className="w-9 h-9 rounded-full shadow-soft group-hover:scale-105 transition-transform"
            width="36"
            height="36"
          />
          <span className="font-display text-2xl tracking-tight text-ink">A Must For Earth</span>
          {/* Grower badge intentionally only in kebab; keeps header clean. */}
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {user && user !== false && (
            <>
              <NavLink to="/scan" data-testid="nav-scan" className={({isActive}) => `px-3 py-2 rounded-full text-sm flex items-center gap-2 transition ${isActive ? "bg-forest text-white" : "text-ink hover:bg-sand-100"}`}>
                <CameraRotate size={16} weight="duotone" /> Scan
              </NavLink>
              <NavLink to="/plants" data-testid="nav-plants" className={({isActive}) => `px-3 py-2 rounded-full text-sm flex items-center gap-2 transition ${isActive ? "bg-forest text-white" : "text-ink hover:bg-sand-100"}`}>
                <Plant size={16} weight="duotone" /> Plants
              </NavLink>
              <NavLink to="/history" data-testid="nav-history" className={({isActive}) => `px-3 py-2 rounded-full text-sm flex items-center gap-2 transition ${isActive ? "bg-forest text-white" : "text-ink hover:bg-sand-100"}`}>
                <ChartLineUp size={16} weight="duotone" /> History
              </NavLink>
              <NavLink to="/settings" data-testid="nav-settings" className={({isActive}) => `px-3 py-2 rounded-full text-sm flex items-center gap-2 transition ${isActive ? "bg-forest text-white" : "text-ink hover:bg-sand-100"}`}>
                <GearSix size={16} weight="duotone" /> Settings
              </NavLink>
              {user.role === "admin" && (
                <NavLink to="/admin" data-testid="nav-admin" className={({isActive}) => `px-3 py-2 rounded-full text-sm flex items-center gap-2 transition ${isActive ? "bg-ink text-white" : "text-ink hover:bg-sand-100"}`}>
                  <ShieldStar size={16} weight="duotone" /> Admin
                </NavLink>
              )}
            </>
          )}
          <NavLink to="/pricing" data-testid="nav-pricing" className={({isActive}) => `px-3 py-2 rounded-full text-sm transition ${isActive ? "bg-forest text-white" : "text-ink hover:bg-sand-100"}`}>
            Pricing
          </NavLink>
          <NavLink to="/blog" data-testid="nav-blog" className={({isActive}) => `px-3 py-2 rounded-full text-sm transition ${isActive ? "bg-forest text-white" : "text-ink hover:bg-sand-100"}`}>
            Blog
          </NavLink>
          <NavLink to="/research" data-testid="nav-research" className={({isActive}) => `px-3 py-2 rounded-full text-sm transition ${isActive ? "bg-forest text-white" : "text-ink hover:bg-sand-100"}`}>
            Research
          </NavLink>
        </nav>

        <div className="flex items-center gap-2">
          {user && user !== false ? (
            <>
              <LanguageMenu />
              <Link to="/notifications" className="relative w-10 h-10 rounded-full bg-sand-100 hover:bg-sand-200 flex items-center justify-center transition" data-testid="navbar-bell" aria-label="Notifications">
                <Bell size={18} weight="duotone" className="text-ink" />
                {unread > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-terra text-white text-[10px] font-bold flex items-center justify-center" data-testid="bell-count">
                    {unread > 9 ? "9+" : unread}
                  </span>
                )}
              </Link>
              <div className="hidden sm:flex items-center gap-2 text-sm text-mute pl-2">
                <UserCircle size={20} weight="duotone" className="text-forest" />
                <span data-testid="navbar-user-email">{user.email}</span>
              </div>
              <div className="hidden lg:block">
                <button onClick={onLogout} data-testid="navbar-logout" className="btn-secondary !py-2 !px-4 !text-sm">
                  <SignOut size={14} /> Sign out
                </button>
              </div>

              {/* Mobile + tablet kebab (< lg) — pulls in Scan / Plants / History / Settings */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    aria-label="Menu"
                    data-testid="navbar-kebab-auth"
                    className="lg:hidden w-10 h-10 rounded-full bg-sand-100 hover:bg-sand-200 flex items-center justify-center text-ink transition"
                  >
                    <List size={20} weight="bold" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-sand-50 border-sand-200 w-60">
                  {isPro && (
                    <>
                      <div
                        className="px-2 py-2 flex items-center gap-2"
                        data-testid="kebab-auth-pro-badge"
                      >
                        <span className="tag !bg-ochre/20 !text-ochre-dark !border-ochre/40 inline-flex items-center gap-1">
                          <Crown size={10} weight="fill" /> Grower
                        </span>
                        <span className="text-xs text-mute truncate">{user.email}</span>
                      </div>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  <DropdownMenuItem asChild data-testid="kebab-auth-scan" className="cursor-pointer focus:bg-sand-100">
                    <Link to="/scan" className="flex items-center gap-2">
                      <CameraRotate size={16} weight="duotone" className="text-forest" />
                      <span>Scan</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild data-testid="kebab-auth-plants" className="cursor-pointer focus:bg-sand-100">
                    <Link to="/plants" className="flex items-center gap-2">
                      <Plant size={16} weight="duotone" className="text-forest" />
                      <span>Plants</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild data-testid="kebab-auth-history" className="cursor-pointer focus:bg-sand-100">
                    <Link to="/history" className="flex items-center gap-2">
                      <ChartLineUp size={16} weight="duotone" className="text-forest" />
                      <span>History</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild data-testid="kebab-auth-settings" className="cursor-pointer focus:bg-sand-100">
                    <Link to="/settings" className="flex items-center gap-2">
                      <GearSix size={16} weight="duotone" className="text-forest" />
                      <span>Settings</span>
                    </Link>
                  </DropdownMenuItem>
                  {user.role === "admin" && (
                    <DropdownMenuItem asChild data-testid="kebab-auth-admin" className="cursor-pointer focus:bg-sand-100">
                      <Link to="/admin" className="flex items-center gap-2">
                        <ShieldStar size={16} weight="duotone" className="text-ink" />
                        <span>Admin</span>
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild data-testid="kebab-auth-pricing" className="cursor-pointer focus:bg-sand-100">
                    <Link to="/pricing" className="flex items-center gap-2">
                      <Tag size={16} weight="duotone" className="text-forest" />
                      <span>Pricing</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild data-testid="kebab-auth-blog" className="cursor-pointer focus:bg-sand-100">
                    <Link to="/blog" className="flex items-center gap-2">
                      <Newspaper size={16} weight="duotone" className="text-forest" />
                      <span>Blog</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild data-testid="kebab-auth-research" className="cursor-pointer focus:bg-sand-100">
                    <Link to="/research" className="flex items-center gap-2">
                      <BookOpen size={16} weight="duotone" className="text-forest" />
                      <span>Research</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild data-testid="kebab-auth-contact" className="cursor-pointer focus:bg-sand-100">
                    <Link
                      to="/contact"
                      className="flex items-center gap-2"
                    >
                      <EnvelopeSimple size={16} weight="duotone" className="text-forest" />
                      <span>Contact support</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={onLogout}
                    data-testid="kebab-auth-logout"
                    className="cursor-pointer focus:bg-terra focus:text-white"
                  >
                    <SignOut size={16} weight="duotone" className="text-terra" />
                    <span>Sign out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              {/* Desktop (lg+) */}
              <div className="hidden lg:flex items-center gap-2">
                <Link to="/login" data-testid="navbar-login" className="text-sm text-ink hover:text-forest px-3 py-2">Sign in</Link>
                <Link to="/register" data-testid="navbar-register" className="btn-primary !py-2 !px-5 !text-sm">Start scanning</Link>
              </div>

              {/* Mobile + tablet kebab menu (< lg) */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    aria-label="Menu"
                    data-testid="navbar-kebab"
                    className="lg:hidden w-10 h-10 rounded-full bg-sand-100 hover:bg-sand-200 flex items-center justify-center text-ink transition"
                  >
                    <List size={20} weight="bold" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-sand-50 border-sand-200 w-56">
                  <DropdownMenuItem asChild data-testid="kebab-pricing" className="cursor-pointer focus:bg-sand-100">
                    <Link to="/pricing" className="flex items-center gap-2">
                      <Tag size={16} weight="duotone" className="text-forest" />
                      <span>Pricing</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild data-testid="kebab-blog" className="cursor-pointer focus:bg-sand-100">
                    <Link to="/blog" className="flex items-center gap-2">
                      <Newspaper size={16} weight="duotone" className="text-forest" />
                      <span>Blog</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild data-testid="kebab-research" className="cursor-pointer focus:bg-sand-100">
                    <Link to="/research" className="flex items-center gap-2">
                      <BookOpen size={16} weight="duotone" className="text-forest" />
                      <span>Research</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild data-testid="kebab-contact" className="cursor-pointer focus:bg-sand-100">
                    <Link to="/contact" className="flex items-center gap-2">
                      <EnvelopeSimple size={16} weight="duotone" className="text-forest" />
                      <span>Contact</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild data-testid="kebab-signin" className="cursor-pointer focus:bg-sand-100">
                    <Link to="/login" className="flex items-center gap-2">
                      <SignIn size={16} weight="duotone" className="text-forest" />
                      <span>Sign in</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild data-testid="kebab-startscanning" className="cursor-pointer focus:bg-forest focus:text-white">
                    <Link to="/register" className="flex items-center gap-2">
                      <Sparkle size={16} weight="duotone" className="text-ochre" />
                      <span className="font-medium">Start scanning</span>
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
